import React from 'react';

import { OrderCurrency } from '@invenco/common-interface/sales';

import { Separator } from '@/components/core/separator';
import { Skeleton } from '@/components/core/skeleton';
import { formatMoney } from '@/shared/helpers';

import {
  getOrderLineDiscountAmount,
  type OrderLine,
  type OrderLineRow,
} from './order-lines-card-columns';

export type OrderLinesOrderContext = {
  currency?: OrderCurrency;
  channelId?: string | null;
  shippingProfileName?: string | null;
  subtotalPrice?: number | null;
  subtotalDiscounts?: number | null;
  subtotalShipping?: number | null;
  totalPrice?: number | null;
};

type OrderTotalsProps = {
  order: OrderLinesOrderContext;
  lines: OrderLine[] | OrderLineRow[];
  isLoading?: boolean;
};

function roundMoney(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function deriveSubtotal(lines: OrderTotalsProps['lines']) {
  return roundMoney(
    lines
      .filter((line) => !line.assemblyOrderLineId)
      .reduce((sum, line) => sum + (line.qtyOrdered ?? 0) * (line.unitPrice ?? 0), 0),
  );
}

function deriveDiscounts(lines: OrderTotalsProps['lines']) {
  return roundMoney(
    lines
      .filter((line) => !line.assemblyOrderLineId)
      .reduce((sum, line) => sum + getOrderLineDiscountAmount(line), 0),
  );
}

export function OrderTotals({ order, lines, isLoading }: OrderTotalsProps) {
  const currency = order.currency ?? OrderCurrency.AUD;
  const subtotal = order.subtotalPrice ?? deriveSubtotal(lines);
  const discounts = order.subtotalDiscounts ?? deriveDiscounts(lines);
  const shipping = order.subtotalShipping ?? 0;
  const total = order.totalPrice ?? roundMoney(subtotal + shipping - discounts);

  const subtotalRows = [
    { label: 'Subtotal', value: subtotal },
    { label: 'Discounts', value: discounts },
    { label: order.shippingProfileName ?? 'Shipping', value: shipping },
  ];

  return (
    <div className="border-t p-4">
      <dl className="grid grid-cols-[1fr_auto] gap-x-20 gap-y-2 text-right tabular-nums">
        {subtotalRows.map(({ label, value }) => (
          <React.Fragment key={label}>
            <dt>{label}</dt>
            <dd>
              {isLoading ? (
                <Skeleton className="ml-auto h-4 w-20" aria-label="Loading total amount" />
              ) : (
                formatMoney(value, currency)
              )}
            </dd>
          </React.Fragment>
        ))}
        <div className="col-span-full -mx-4 py-2">
          <Separator />
        </div>
        <dt className="font-semibold">Order total</dt>
        <dd className="font-semibold">
          {isLoading ? (
            <Skeleton className="ml-auto h-4 w-20" aria-label="Loading total amount" />
          ) : (
            formatMoney(total, currency)
          )}
        </dd>
      </dl>
    </div>
  );
}
